import { useState } from "react";

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouchDevice: boolean;
  deviceType: "mobile" | "tablet" | "desktop";
  os: "ios" | "android" | "windows" | "macos" | "linux" | "unknown";
  browser: string;
}

/**
 * Get the PHYSICAL screen size (short edge) in CSS pixels.
 * This is stable across orientation changes and "Desktop Site" toggle.
 * Uses screen.width/height (physical) NOT window.innerWidth (viewport).
 */
function getPhysicalShortEdge(): number {
  return Math.min(screen.width, screen.height);
}

/**
 * Comprehensive device detection hook
 * Uses PHYSICAL screen dimensions (not viewport) for reliable detection
 * even when "Desktop Site" is toggled off on Android Chrome.
 */
export function useDeviceDetection(): DeviceInfo {
  // Compute once on mount — physical device doesn't change
  const [deviceInfo] = useState<DeviceInfo>(() => getDeviceInfo());
  return deviceInfo;
}

/**
 * Get device information using physical screen dimensions + UA heuristics.
 * This correctly classifies:
 * - Android phone (short edge < 600px)
 * - Android tablet (short edge >= 600px, touch, Android UA)
 * - iPad (iPad UA or Mac + touch)
 * - Desktop (no touch or large screen without mobile UA)
 */
function getDeviceInfo(): DeviceInfo {
  const userAgent = navigator.userAgent;
  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const shortEdge = getPhysicalShortEdge();

  // ── UA-based detection ──────────────────────────────────────────────
  const isIPhone = /iPhone/i.test(userAgent);
  const isIPod = /iPod/i.test(userAgent);
  const isIPad = /iPad/i.test(userAgent) || 
    (/Macintosh/i.test(userAgent) && isTouchDevice); // iPadOS 13+ reports as Mac
  const isAndroid = /Android/i.test(userAgent);
  const isAndroidPhone = isAndroid && /Mobile/i.test(userAgent);
  const isAndroidTablet = isAndroid && !/Mobile/i.test(userAgent);

  // ── Physical screen-based classification ────────────────────────────
  // Phones: short edge < 600 CSS pixels
  // Tablets: short edge 600-1024 CSS pixels with touch
  // Desktop: no touch or short edge > 1024
  const isPhoneByScreen = shortEdge < 600 && isTouchDevice;
  const isTabletByScreen = shortEdge >= 600 && shortEdge <= 1024 && isTouchDevice;

  // ── Final classification (UA takes priority, screen as fallback) ────
  let isMobile = false;
  let isTablet = false;
  let isDesktop = false;

  if (isIPhone || isIPod || isAndroidPhone) {
    // Explicit phone UA
    isMobile = true;
  } else if (isIPad || isAndroidTablet) {
    // Explicit tablet UA
    isTablet = true;
  } else if (isAndroid && isPhoneByScreen) {
    // Android without "Mobile" in UA but small screen = phone
    isMobile = true;
  } else if (isAndroid && isTabletByScreen) {
    // Android without "Mobile" in UA and tablet-sized screen = tablet
    isTablet = true;
  } else if (isPhoneByScreen) {
    // Fallback: small screen + touch = phone
    isMobile = true;
  } else if (isTabletByScreen) {
    // Fallback: medium screen + touch = tablet
    isTablet = true;
  } else {
    isDesktop = true;
  }

  // Determine device type
  let deviceType: "mobile" | "tablet" | "desktop";
  if (isMobile) {
    deviceType = "mobile";
  } else if (isTablet) {
    deviceType = "tablet";
  } else {
    deviceType = "desktop";
  }

  // ── Detect OS ───────────────────────────────────────────────────────
  let os: DeviceInfo["os"] = "unknown";
  if (/iPhone|iPad|iPod/i.test(userAgent) || (/Macintosh/i.test(userAgent) && isTouchDevice)) {
    os = "ios";
  } else if (/Android/i.test(userAgent)) {
    os = "android";
  } else if (/Win/i.test(userAgent)) {
    os = "windows";
  } else if (/Mac/i.test(userAgent)) {
    os = "macos";
  } else if (/Linux/i.test(userAgent)) {
    os = "linux";
  }

  // ── Detect browser ──────────────────────────────────────────────────
  let browser = "unknown";
  if (/Chrome/i.test(userAgent) && !/Edge|Edg/i.test(userAgent)) {
    browser = "chrome";
  } else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) {
    browser = "safari";
  } else if (/Firefox/i.test(userAgent)) {
    browser = "firefox";
  } else if (/Edge|Edg/i.test(userAgent)) {
    browser = "edge";
  }

  return {
    isMobile,
    isTablet,
    isDesktop,
    isTouchDevice,
    deviceType,
    os,
    browser,
  };
}

/**
 * Standalone function to check if device is a phone (NOT tablet).
 * Uses physical screen dimensions for reliable detection.
 */
export function isMobileDevice(): boolean {
  const userAgent = navigator.userAgent;
  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const shortEdge = getPhysicalShortEdge();

  // Explicit phone UAs
  if (/iPhone|iPod/i.test(userAgent)) return true;
  if (/Android/i.test(userAgent) && /Mobile/i.test(userAgent)) return true;

  // iPad is NOT mobile
  if (/iPad/i.test(userAgent) || (/Macintosh/i.test(userAgent) && isTouchDevice)) return false;

  // Android tablet (no "Mobile" in UA) is NOT mobile
  if (/Android/i.test(userAgent) && shortEdge >= 600) return false;

  // Fallback: small physical screen + touch = mobile
  return shortEdge < 600 && isTouchDevice;
}

/**
 * iPhone, iPad or iPod — including an iPad that reports itself as a Mac.
 * Same rule as `useDeviceDetection`, for code that is not a component.
 */
export function isIOSDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const touch = typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && touch);
}
